// =============================================================================
// components/ui/ProgressRow.tsx — a labeled progress row: [label ......  value]
// over a thin full-width track tinted by the status color, with a % pill.
//
// Reference: the macro-tracker screenshot — stacked labeled bars (Protein /
// Carbs / Fat …) each with a right-aligned value and a colored fill whose hue
// encodes how close the metric is to goal. Uses statusColor(pct) from
// constants/theme so the tint (success/info/warning/error) is a single source.
//
// Two theme modes (design-system rule): 'light' (default) + 'dark'. Pure View
// composition (no glass needed for the track) so it drops into any card.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { colors, spacing, fonts, statusColor, colorTokens, ThemeMode } from '../../constants/theme';

export interface ProgressRowProps {
  label: string;
  /** Progress fraction (0–1) or percentage (0–100); statusColor normalizes it. */
  progress: number;
  /** Right-aligned value text, e.g. "82 / 120g". */
  value?: string;
  /** Show the % pill on the right of the label row. Default true. */
  showPercent?: boolean;
  /** Override the track tint (defaults to statusColor(progress)). */
  color?: string;
  theme?: ThemeMode;
  style?: StyleProp<ViewStyle>;
}

export default function ProgressRow({
  label, progress, value, showPercent = true, color, theme = 'light', style,
}: ProgressRowProps) {
  const p = progress > 1 ? progress / 100 : progress;
  const clamped = Math.max(0, Math.min(1, p));
  const tint = color ?? statusColor(progress);
  const tc = colorTokens[theme];
  const dark = theme === 'dark';
  const pctText = `${Math.round(clamped * 100)}%`;

  return (
    <View style={[styles.wrap, style]}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, { color: tc.text }]} numberOfLines={1}>{label}</Text>
        <View style={styles.right}>
          {value ? <Text style={[styles.value, { color: tc.textMuted }]} numberOfLines={1}>{value}</Text> : null}
          {showPercent ? (
            <View style={[styles.pct, { backgroundColor: tint + (dark ? '2E' : '22') }]}>
              <Text style={[styles.pctText, { color: tint }]}>{pctText}</Text>
            </View>
          ) : null}
        </View>
      </View>
      <View style={[styles.track, { backgroundColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(26,26,46,0.08)' }]}>
        <View style={[styles.fill, { width: `${clamped * 100}%`, backgroundColor: tint }]} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.xs + 2 },
  labelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  label: { ...fonts.semibold, fontSize: 14, flexShrink: 1 },
  right: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  value: { ...fonts.medium, fontSize: 13 },
  pct: {
    minWidth: 40,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 9999,
    alignItems: 'center',
  },
  pctText: { ...fonts.bold, fontSize: 11 },
  track: { height: 6, borderRadius: 3, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 3 },
});
